import React, { useState, ChangeEvent, useMemo, useCallback } from 'react';
import {
  Box,
  TextField,
  Typography,
  IconButton,
  CircularProgress,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import { NavigationField, NavigationGroup } from '../edit/navigation/types';
import { useFieldHidden, ListingResource } from '@brightmls/nextgen.lae.flc';
import { useMetadata } from '../../contexts/MetadataContext';
import { PropertyField } from '../../system/field';
import { FeatureFlags } from 'src/flags';
import { useFeatureFlags } from 'src/contexts/FeatureFlagsContext';

export interface SearchFieldResult {
  type: 'field' | 'lookup';
  field: NavigationField;
  groupName: string;
  sectionName: string;
  lookupValue?: string;
  lookupKey?: string;
  menuIsExpanded?: boolean;
}

interface SearchDropDownProps {
  data: NavigationGroup[];
  onClick: (groupName: string, fieldKey: string) => void;
  onResultsChange?: (results: PropertyField[] | null) => void;
  menuIsExpanded?: boolean;
}

export const EXCLUDED_SEARCH_LOOKUPS = [
  'Counties_or_Regions', // County lookup
];

const SearchDropDown: React.FC<SearchDropDownProps> = ({
  data,
  onClick,
  onResultsChange,
  menuIsExpanded,
}) => {
  const [value, setValue] = useState<string>('');
  const [filteredResults, setFilteredResults] = useState<SearchFieldResult[]>(
    [],
  );
  const [isLoading, setIsLoading] = useState(false);

  // Use the new optimized hook
  const isFieldHidden = useFieldHidden();
  const { lookup } = useMetadata();
  const { hasFeature } = useFeatureFlags();
  const hasSidepanelRedesign = hasFeature(
    FeatureFlags.NGW_EDIT_SIDEPANEL_REDESIGN,
  );

  const showSearchResults = useCallback(
    (value: string) => {
      if (value.length < 3) return false;
      if (hasSidepanelRedesign) {
        // if menuIsExpanded is not definted, default to showing the search results
        if (typeof menuIsExpanded === 'undefined') return true;
        // otherwise match expansion boolean
        return menuIsExpanded;
      }
      return true;
    },
    [hasSidepanelRedesign, menuIsExpanded],
  );

  // Memoize searchable fields to avoid recomputing on every render
  const searchableFields = useMemo((): SearchFieldResult[] => {
    const fields: SearchFieldResult[] = [];

    data.forEach((group) => {
      group.sections.forEach((section) => {
        section.fields.forEach((fieldItem) => {
          // Only include searchable and visible fields
          if (
            fieldItem?.definition?.Searchable &&
            !isFieldHidden(
              ListingResource.Property,
              fieldItem.key as PropertyField,
            )
          ) {
            // Add the field itself
            fields.push({
              type: 'field',
              field: fieldItem,
              groupName: group.name,
              sectionName: section.name,
            });

            // Add lookup values if applicable
            if (
              fieldItem?.lookupKey &&
              !EXCLUDED_SEARCH_LOOKUPS.includes(fieldItem?.lookupKey)
            ) {
              const lookupValues = lookup(fieldItem.lookupKey);
              if (lookupValues) {
                lookupValues.forEach((lookupValue) => {
                  fields.push({
                    type: 'lookup',
                    field: fieldItem,
                    groupName: group.name,
                    sectionName: section.name,
                    lookupValue: lookupValue.LookupValue,
                    lookupKey: lookupValue.LookupKey,
                  });
                });
              }
            }
          }
        });
      });
    });

    return fields;
  }, [data, isFieldHidden, lookup]); // Only recompute when these change

  const handleSearch = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const searchValue = event.target.value;
      setValue(searchValue);
      setIsLoading(true);

      if (searchValue.length >= 3) {
        const searchTerm = searchValue.toLowerCase();

        // Use pre-computed searchable fields
        const results = searchableFields.filter((result) => {
          if (result.type === 'field') {
            return result.field.label.toLowerCase().includes(searchTerm);
          } else {
            const searchText = `${result.lookupValue}`.toLowerCase();
            return searchText.includes(searchTerm);
          }
        });

        setFilteredResults(results);

        if (onResultsChange) {
          // Dedupe field keys since lookups can repeat them
          const uniqueFieldKeys = [
            ...new Set(
              results.map((result) => result.field.key as PropertyField),
            ),
          ];
          onResultsChange(uniqueFieldKeys);
        }
      } else {
        setFilteredResults([]);
        if (onResultsChange) {
          onResultsChange(null);
        }
      }

      setIsLoading(false);
    },
    [searchableFields, onResultsChange],
  );

  const handleClear = useCallback(() => {
    setValue('');
    setFilteredResults([]);
    if (onResultsChange) {
      onResultsChange(null);
    }
  }, [onResultsChange]);

  const handleResultClick = useCallback(
    (result: SearchFieldResult) => {
      onClick(result.groupName, result.field.key);
    },
    [onClick],
  );

  // Memoize the result item component
  const SearchResultItem = useMemo(
    () =>
      React.memo<{ result: SearchFieldResult }>(({ result }) => {
        return (
          <Box
            sx={{
              cursor: 'pointer',
              '&:hover': { backgroundColor: '#0000000A' },
              padding: '8px 16px',
            }}
            onClick={() => handleResultClick(result)}
          >
            {result.type === 'lookup' ? (
              <Box>
                <Typography sx={{ fontWeight: 500 }}>
                  {result.lookupValue}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {result.field.label}
                </Typography>
              </Box>
            ) : (
              <Box>
                <Typography sx={{ fontWeight: 500 }}>
                  {result.field.label}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {result.sectionName}
                </Typography>
              </Box>
            )}
          </Box>
        );
      }),
    [handleResultClick],
  );

  const featureFlagStyles = {
    display: 'flex',
    alignItems: 'center',
    borderRadius: '8px',
    padding: '2px',
    width: menuIsExpanded ? '258px' : '48px',
    margin: 'auto',
    border: '1.5px solid',
    borderColor: 'secondary.medium',
    opacity: '1',
    '& .MuiFormControl-root': {
      '& .MuiInputBase-root': {
        paddingLeft: '10px!important',
        '& .MuiOutlinedInput-notchedOutline': {
          border: 'none!important',
        },
      },
    },
  };

  const defaultStylesBox = {
    display: 'flex',
    alignItems: 'center',
    marginBottom: '5px',
  };

  const defaultStylesTextField = {
    paddingBottom: '2px',
    paddingTop: '4px',
    '& .MuiInputBase-input::placeholder': {
      color: 'secondary.medium', // Change to your desired color
      opacity: 1, // Optional: makes sure it's not faded
    },
    '& .MuiInputBase-input': {
      padding: '8px 12px',
      fontSize: '14px',
      color: 'secondary.medium',
    },
  };
  return (
    <Box
      component="form"
      noValidate
      autoComplete="off"
      sx={{
        position: 'relative',
        px: '5px',
      }}
    >
      <Box
        id="nav-search-box"
        sx={{
          ...(hasSidepanelRedesign ? featureFlagStyles : defaultStylesBox),
        }}
      >
        <TextField
          fullWidth
          id="nav-search"
          sx={{
            ...(hasSidepanelRedesign
              ? defaultStylesTextField
              : {
                  paddingLeft: '10px',
                }),
          }}
          variant="outlined"
          placeholder="Find a Field or Value"
          value={value}
          onChange={handleSearch}
          InputProps={{
            startAdornment: (
              <SearchIcon
                fill="secondary.medium"
                sx={{
                  marginLeft: '0px',
                  marginRight: '10px',
                  color: 'secondary.medium',
                }}
              />
            ),
            endAdornment: value && (
              <IconButton
                onClick={handleClear}
                size="small"
                sx={{
                  paddingLeft: '10px',
                  fill: 'secondary.medium',
                  color: 'secondary.medium',
                }}
              >
                <CloseIcon
                  fill="secondary.medium"
                  sx={{
                    padding: '5px',
                  }}
                />
              </IconButton>
            ),
          }}
        />
      </Box>

      {showSearchResults(value) && (
        <Box
          sx={{
            position: 'absolute',
            top: '100%',
            left: 0,
            right: 0,
            marginTop: '5px',
            boxShadow: 3,
            borderRadius: 1,
            background: 'white',
            zIndex: 1000,
            maxHeight: '300px',
            overflowY: 'auto',
          }}
        >
          {isLoading ? (
            <Box sx={{ p: 2, textAlign: 'center' }}>
              <CircularProgress size={20} />
            </Box>
          ) : filteredResults.length > 0 ? (
            filteredResults.map((result, index) => (
              <SearchResultItem
                key={`${result.type}-${result.field.key}-${index}`}
                result={result}
              />
            ))
          ) : (
            <Box sx={{ padding: '8px 16px' }}>
              <Typography>No results found</Typography>
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
};

export default SearchDropDown;
